/**
 * Tarjeta de un pedido en el tablero (réplica del `pedido_card` del SSR).
 *
 * Muestra el material (categoría + referencias), la cantidad, la descripción y los
 * metadatos (sede/autor/fecha) y, debajo, las **acciones disponibles según permiso
 * y estado** (igual que el SSR, `docs/frontend_architecture.md`, Pr. 5):
 *  - Enviar: en la pestaña "Por enviar" (pendiente), con `send_pedidos_laminas`.
 *  - Editar / Eliminar: solo admin y solo pendientes (`edit_*` / `delete_*`).
 *  - Recibir: en la pestaña "En camino", con `receive_pedidos_laminas`.
 *
 * Cada acción abre un `ConfirmActionDialog` y, al confirmar, ejecuta su mutación
 * (que invalida el tablero). Presentacional + orquestación de hooks de datos: no
 * hace `fetch`. El `variant` decide qué metadatos y acciones aplican por columna.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { pedidoEditPath } from "@/features/navigation";

import {
  DELETE_PERMISSION,
  EDIT_PERMISSION,
  RECEIVE_PERMISSION,
  SEND_PERMISSION,
} from "../constants";
import { formatDateTimeShort, referenciasDePedido } from "../format";
import {
  useEliminarPedido,
  useEnviarPedido,
  useRecibirPedido,
} from "../hooks/usePedidoMutations";
import type { PedidoListItem } from "../types";
import { ConfirmActionDialog } from "./ConfirmActionDialog";

/** Columna del tablero a la que pertenece la tarjeta (decide acciones/metadatos). */
export type PedidoCardVariant = "por-enviar" | "en-curso" | "en-camino";

interface PedidoCardProps {
  pedido: PedidoListItem;
  variant: PedidoCardVariant;
}

/** Diálogo abierto en la tarjeta (o `null`). */
type OpenDialog = "enviar" | "recibir" | "eliminar" | null;

export function PedidoCard({ pedido, variant }: PedidoCardProps) {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [open, setOpen] = useState<OpenDialog>(null);

  const enviar = useEnviarPedido();
  const recibir = useRecibirPedido();
  const eliminar = useEliminarPedido();

  const refs = referenciasDePedido(pedido);
  const stripe =
    variant === "en-camino"
      ? "bg-brand"
      : "bg-amber-500";

  // Acciones disponibles (permiso + estado + columna), espejo del SSR.
  const canSend = variant === "por-enviar" && hasPermission(SEND_PERMISSION);
  const canEdit =
    variant !== "en-camino" &&
    isAdmin &&
    hasPermission(EDIT_PERMISSION) &&
    pedido.estado === "pendiente";
  const canDelete =
    variant !== "en-camino" &&
    isAdmin &&
    hasPermission(DELETE_PERMISSION) &&
    pedido.estado === "pendiente";
  const canReceive = variant === "en-camino" && hasPermission(RECEIVE_PERMISSION);

  const hasActions = canSend || canEdit || canDelete || canReceive;

  function closeDialog() {
    setOpen(null);
    enviar.reset();
    recibir.reset();
    eliminar.reset();
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <div className={`h-1 ${stripe}`} />
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-bold text-brand">
              {pedido.categoria}
            </span>
            {refs.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {refs.map((ref) => (
                  <span
                    key={ref}
                    className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500"
                  >
                    {ref}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0 text-right">
            <div className="text-xl font-extrabold leading-none text-slate-900">
              {pedido.cantidad}
            </div>
            <div className="text-[0.65rem] font-medium text-slate-500">unidades</div>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {variant === "en-camino" ? (
            <>
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
                Desde: {pedido.origen_sede?.nombre ?? "—"}
              </span>
              {pedido.fecha_envio && <span>{formatDateTimeShort(pedido.fecha_envio)}</span>}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">
              {pedido.destino_sede?.nombre ?? "—"}
            </span>
          )}
        </div>
      </div>

      {hasActions && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2">
          {canSend && (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => {
                enviar.reset();
                setOpen("enviar");
              }}
            >
              Enviar
            </Button>
          )}
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => navigate(pedidoEditPath(pedido.id))}
            >
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              size="sm"
              variant="danger"
              onClick={() => {
                eliminar.reset();
                setOpen("eliminar");
              }}
            >
              Eliminar
            </Button>
          )}
          {canReceive && (
            <Button
              type="button"
              size="sm"
              variant="primary"
              className="w-full"
              onClick={() => {
                recibir.reset();
                setOpen("recibir");
              }}
            >
              Marcar como recibido
            </Button>
          )}
        </div>
      )}

      <ConfirmActionDialog
        open={open === "enviar"}
        onOpenChange={(o) => (o ? setOpen("enviar") : closeDialog())}
        title={`Enviar pedido · ${pedido.categoria}`}
        description="¿Marcar este pedido como enviado (en camino)?"
        confirmLabel="Enviar"
        isPending={enviar.isPending}
        error={enviar.isError ? enviar.error : null}
        onConfirm={() =>
          enviar.mutate(pedido.id, { onSuccess: () => setOpen(null) })
        }
      />

      <ConfirmActionDialog
        open={open === "recibir"}
        onOpenChange={(o) => (o ? setOpen("recibir") : closeDialog())}
        title={`Recibir pedido · ${pedido.categoria}`}
        description="¿Marcar este pedido como recibido? Si tiene referencia, se sumará al inventario."
        confirmLabel="Recibir"
        isPending={recibir.isPending}
        error={recibir.isError ? recibir.error : null}
        onConfirm={() =>
          recibir.mutate(pedido.id, { onSuccess: () => setOpen(null) })
        }
      />

      <ConfirmActionDialog
        open={open === "eliminar"}
        onOpenChange={(o) => (o ? setOpen("eliminar") : closeDialog())}
        title={`Eliminar pedido · ${pedido.categoria}`}
        description="¿Eliminar este pedido pendiente de forma permanente? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        isPending={eliminar.isPending}
        error={eliminar.isError ? eliminar.error : null}
        onConfirm={() =>
          eliminar.mutate(pedido.id, { onSuccess: () => setOpen(null) })
        }
      />
    </div>
  );
}
